import './styles.scss'
import { CSSTransition } from 'react-transition-group'
import { useRef, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useForm } from '../../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import favicon from '../../../Assets/Images/favicon.png'

const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID
const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_QUOTE
const publicId   = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const SERVICES = [
  'Air Freight',
  'Ocean Freight',
  'Cargo Transportation',
  'Packaging and Storage',
]

const CARGO_TYPES = [
  'General Goods',
  'Electronics',
  'Perishables',
  'Hazardous Materials',
  'Vehicles',
  'Other',
]

const QuoteForm = ({ showForm, handleClose }) => {
  const formRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)

  const handleClick = (e) => e?.stopPropagation()

  const { form, reset, onChange } = useForm({
    name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    service: '',
    cargo: '',
    weight: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { return reset }, [])

  useEffect(() => {
    if (!showForm) {
      setSubmitted(false)
    }
  }, [showForm])

  const handleSubmit = async (e) => {
    e.preventDefault()
    onChange('loading', true)
    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      setSubmitted(true)
      reset()
    } catch (error) {
      console.error('EmailJS error', error)
    }
    onChange('loading', false)
  }

  return (
    <CSSTransition
      in={showForm}
      nodeRef={formRef}
      timeout={300}
      unmountOnExit
      classNames="quote-form"
    >
      <div ref={formRef} className="quote-form" onClick={handleClick}>
        <button className="quote-form__close" onClick={handleClose} aria-label="Close">
          <FontAwesomeIcon icon={solid('xmark')} />
        </button>

        <div className="quote-form__sidebar">
          <div className="quote-form__sidebar__inner">
            <div className="quote-form__sidebar__icon">
              <img src={favicon} alt="Stanbit Logistics" />
            </div>
            <h2>Get A Free Quote</h2>
            <p>Tell us about your shipment and we'll provide a competitive quote within 24 hours.</p>

            <ul className="quote-form__sidebar__perks">
              <li><FontAwesomeIcon icon={solid('circle-check')} /> Fast turnaround — 24 hr response</li>
              <li><FontAwesomeIcon icon={solid('circle-check')} /> Competitive rates worldwide</li>
              <li><FontAwesomeIcon icon={solid('circle-check')} /> Door-to-door tracking</li>
              <li><FontAwesomeIcon icon={solid('circle-check')} /> Dedicated account manager</li>
            </ul>

            <div className="quote-form__sidebar__contact">
              <FontAwesomeIcon icon={solid('phone')} />
              <span>+1 (512) 643-7634</span>
            </div>
          </div>
        </div>

        <div className="quote-form__body">
          {submitted ? (
            <div className="quote-form__success">
              <div className="quote-form__success__icon">
                <FontAwesomeIcon icon={solid('circle-check')} />
              </div>
              <h3>Quote Request Sent!</h3>
              <p>We've received your request and will get back to you within 24 hours with a competitive quote.</p>
              <button onClick={handleClose}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h3>Shipment Details</h3>
              <p className="quote-form__body__sub">Fields marked <span>*</span> are required.</p>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-name">Full Name <span>*</span></label>
                  <input id="qf-name" name="name" type="text" placeholder="John Doe" required value={form.name} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-email">Email Address <span>*</span></label>
                  <input id="qf-email" name="email" type="email" placeholder="john@example.com" required value={form.email} onChange={handleChange} />
                </div>
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-phone">Phone Number</label>
                  <input id="qf-phone" name="phone" type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-service">Service Type <span>*</span></label>
                  <select id="qf-service" name="service" required value={form.service} onChange={handleChange}>
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-origin">Origin <span>*</span></label>
                  <input id="qf-origin" name="origin" type="text" placeholder="City, Country" required value={form.origin} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-dest">Destination <span>*</span></label>
                  <input id="qf-dest" name="destination" type="text" placeholder="City, Country" required value={form.destination} onChange={handleChange} />
                </div>
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-cargo">Cargo Type</label>
                  <select id="qf-cargo" name="cargo" value={form.cargo} onChange={handleChange}>
                    <option value="">Select cargo type…</option>
                    {CARGO_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-weight">Estimated Weight / Volume</label>
                  <input id="qf-weight" name="weight" type="text" placeholder="e.g. 500 kg or 2 CBM" value={form.weight} onChange={handleChange} />
                </div>
              </div>

              <div className="qf-field">
                <label htmlFor="qf-msg">Additional Requirements</label>
                <textarea id="qf-msg" name="msg" rows={3} placeholder="Special handling, insurance needs, preferred timeline…" value={form.msg} onChange={handleChange} />
              </div>

              <button
                type="submit"
                className={`quote-form__submit${form.loading ? ' loading' : ''}`}
                disabled={form.loading}
              >
                {form.loading ? (
                  <><FontAwesomeIcon icon={solid('spinner')} className="spin" /> Sending…</>
                ) : (
                  <><FontAwesomeIcon icon={solid('paper-plane')} /> Request My Quote</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export default QuoteForm
